// 작성자 표시 공용 컴포넌트.
//
// 모든 도메인 (일정/출장/장애/장애코멘트/자재/사진/네트워크장비) 의 List/Detail 에
// 동일한 시각 언어로 작성자를 표시하기 위한 단일 진입점. 사용 예:
//
//   <AuthorChip name={schedule.created_by_name} userId={schedule.created_by_id} />
//
// - name 이 없으면 "작성자 미상" fallback (legacy 데이터 호환)
// - userId 가 있으면 profile_image_url 을 자동 빌드 (URL 만 알면 됨, fetch 안 함)
// - dense prop 으로 목록용 (height 22) / 상세용 (height 28) 두 가지 크기
//
// 이미지 src 는 backend 가 builder.go 에서 만드는 동일 포맷:
//   /v0.1/auth/users/{id}/profile-image?v={fid}
// fid 가 없으면 plain `/v0.1/auth/users/{id}/profile-image` 로 fallback —
// 백엔드는 v 가 없어도 동작하지만 캐싱이 약해진다 (큰 문제는 아님).

import React from 'react';
import { Avatar, Chip } from '@mui/material';
import { withMediaToken } from '../utils/media';
import { colorFromSeed } from '../utils/colorFromSeed';

export interface AuthorChipProps {
  /** 작성 시점 사용자 이름 스냅샷. 없으면 "작성자 미상". */
  name?: string | null;
  /** 작성 시점 user_id. 있으면 프로필 이미지 자동 plumb-in. 없으면 이니셜 fallback. */
  userId?: number | null;
  /** 작성자 칩에 표시할 prefix label (기본 "작성자"). 빈 문자열이면 prefix 없음. */
  label?: string;
  /** true 면 작은 칩 (높이 22). false (기본) 는 detail 용 (높이 28). */
  dense?: boolean;
}

function initialsOf(name?: string | null): string {
  const src = (name || '').trim();
  if (!src) return '?';
  if (/[가-힣]/.test(src)) return src.charAt(0);
  const parts = src.split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return src.charAt(0).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function profileImageUrlFor(userId?: number | null): string | undefined {
  if (!userId || userId <= 0) return undefined;
  // 캐시 무효화용 v 가 없어도 백엔드는 동작. 새 이미지 업로드 후엔 caller 가
  // 페이지를 새로고침해 받아간 created_by 메타로 표시되므로 자연스럽게 갱신됨.
  // `<img>` 는 Authorization 헤더를 못 붙이므로 ?token= 쿼리를 실어 보낸다.
  return withMediaToken(`/v0.1/auth/users/${userId}/profile-image`);
}

const AuthorChip: React.FC<AuthorChipProps> = ({ name, userId, label = '작성자', dense = false }) => {
  const display = name && name.trim() ? name : '작성자 미상';
  const initials = initialsOf(name);
  const bg = colorFromSeed(name || String(userId || 'anon'));
  const src = profileImageUrlFor(userId);
  const size = dense ? 18 : 22;
  const fontSize = dense ? '0.7rem' : '0.75rem';

  return (
    <Chip
      size="small"
      variant="outlined"
      label={label ? `${label}: ${display}` : display}
      title={label ? `${label}: ${display}` : display}
      avatar={
        <Avatar
          src={src}
          sx={{ width: size, height: size, fontSize, bgcolor: bg, color: '#fff' }}
          // 이미지 로드 실패시 onError 가 src 를 비워 이니셜 fallback 됨.
          imgProps={{ onError: (e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; } }}
        >
          {initials}
        </Avatar>
      }
      sx={{
        height: dense ? 22 : 28,
        fontSize: dense ? '0.72rem' : '0.78rem',
        // outline 칩의 background 가 비어 보이지 않게 살짝.
        bgcolor: 'background.paper',
        '& .MuiChip-avatar': { ml: 0.25, mr: -0.5 },
      }}
    />
  );
};

export default AuthorChip;
